// Geolocation helper with timeout and high-accuracy preference.
// Returns a Promise that always resolves to an object describing success or failure
// (never rejects), so calling code can decide how to proceed.

const DEFAULT_TIMEOUT_MS = 12000;
const DEFAULT_MAX_AGE_MS = 30000;

/**
 * @typedef {Object} GeoResult
 * @property {boolean} ok
 * @property {number|null} latitude
 * @property {number|null} longitude
 * @property {number|null} accuracy_m
 * @property {string|null} error - "denied" | "unavailable" | "timeout" | "unsupported" | string
 * @property {string|null} errorMessage
 */

/**
 * Captures the current device location.
 * @param {{ timeoutMs?: number, maximumAgeMs?: number, enableHighAccuracy?: boolean }} options
 * @returns {Promise<GeoResult>}
 */
export function getCurrentLocation(options = {}) {
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const maximumAgeMs = options.maximumAgeMs ?? DEFAULT_MAX_AGE_MS;
    const enableHighAccuracy = options.enableHighAccuracy ?? true;

    return new Promise((resolve) => {
        if (!("geolocation" in navigator)) {
            resolve({
                ok: false,
                latitude: null,
                longitude: null,
                accuracy_m: null,
                error: "unsupported",
                errorMessage: "Este dispositivo no soporta geolocalización",
            });
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                resolve({
                    ok: true,
                    latitude: pos.coords.latitude,
                    longitude: pos.coords.longitude,
                    accuracy_m: pos.coords.accuracy ?? null,
                    error: null,
                    errorMessage: null,
                });
            },
            (err) => {
                const map = {
                    1: "denied",       // PERMISSION_DENIED
                    2: "unavailable",  // POSITION_UNAVAILABLE
                    3: "timeout",      // TIMEOUT
                };
                resolve({
                    ok: false,
                    latitude: null,
                    longitude: null,
                    accuracy_m: null,
                    error: map[err.code] || "unknown",
                    errorMessage: err.message || "Error de geolocalización",
                });
            },
            { enableHighAccuracy, timeout: timeoutMs, maximumAge: maximumAgeMs },
        );
    });
}

export function formatDistance(meters) {
    if (meters == null || isNaN(meters)) return "—";
    if (meters < 1000) return `${Math.round(meters)} m`;
    return `${(meters / 1000).toFixed(2)} km`;
}

export function mapsLink(lat, lng) {
    if (lat == null || lng == null) return null;
    return `https://www.google.com/maps?q=${lat},${lng}`;
}
